export type PrizeId =
  | '50k-challenge'
  | '10-voucher'
  | '100k-challenge'
  | '50k-funded'
  | '100k-funded';

export type PrizeBodyVariant = 'challenge' | 'funded' | 'voucher';

export interface Prize {
  id: PrizeId;
  weight: number;
  segmentTop: string;
  segmentBottom: string;
  segmentFill: string;
  segmentInk: 'light' | 'dark';
  statue: string;
  cardLabel: string;
  cardName: string;
  cardDescription: string;
  tableLabel: string;
  modalAmount: string;
  modalQualifier: string;
  modalBodyVariant: PrizeBodyVariant;
  isTopPrize?: boolean;
}

export const PRIZES: readonly Prize[] = [
  {
    id: '50k-challenge',
    weight: 50,
    segmentTop: '50K',
    segmentBottom: 'Challenge',
    segmentFill: '#317F6A',
    segmentInk: 'light',
    statue: '/prize-wheel/statue-50k-challenge.png',
    cardLabel: 'Common',
    cardName: '50K Challenge',
    cardDescription: 'Fresh $50K evaluation account, standard rules.',
    tableLabel: '50K Challenge account',
    modalAmount: '$50,000',
    modalQualifier: 'Challenge account',
    modalBodyVariant: 'challenge',
  },
  {
    id: '10-voucher',
    weight: 30,
    segmentTop: '10%',
    segmentBottom: 'Voucher',
    segmentFill: '#0F3037',
    segmentInk: 'light',
    statue: '/prize-wheel/statue-10-voucher.png',
    cardLabel: 'Bonus',
    cardName: '10% Voucher',
    cardDescription: 'Code applied at your next checkout.',
    tableLabel: '10% voucher for the next purchase',
    modalAmount: '10%',
    modalQualifier: 'discount voucher',
    modalBodyVariant: 'voucher',
  },
  {
    id: '100k-challenge',
    weight: 13,
    segmentTop: '100K',
    segmentBottom: 'Challenge',
    segmentFill: '#19A27F',
    segmentInk: 'light',
    statue: '/prize-wheel/statue-100k-challenge.png',
    cardLabel: 'Uncommon',
    cardName: '100K Challenge',
    cardDescription: 'Fresh $100K evaluation, standard rules.',
    tableLabel: '100K Challenge account',
    modalAmount: '$100,000',
    modalQualifier: 'Challenge account',
    modalBodyVariant: 'challenge',
  },
  {
    id: '50k-funded',
    weight: 6,
    segmentTop: '50K',
    segmentBottom: 'Funded',
    segmentFill: '#899B82',
    segmentInk: 'light',
    statue: '/prize-wheel/statue-50k-funded.png',
    cardLabel: 'Rare',
    cardName: '50K Funded',
    cardDescription: 'Live $50K Funded account, no evaluation needed.',
    tableLabel: '50K Funded account',
    modalAmount: '$50,000',
    modalQualifier: 'Funded account',
    modalBodyVariant: 'funded',
  },
  {
    id: '100k-funded',
    weight: 1,
    segmentTop: '100K',
    segmentBottom: 'Funded',
    segmentFill: '#DCF5F0',
    segmentInk: 'dark',
    statue: '/prize-wheel/statue-100k-funded.png',
    cardLabel: 'Top Prize',
    cardName: '100K Funded Account',
    cardDescription:
      "The rarest slot on the wheel. A live Funded account with a $100,000 balance and IQ Capital's 90/10 profit split — no evaluation\nphase required.",
    tableLabel: '100K Funded account',
    modalAmount: '$100,000',
    modalQualifier: 'Funded account',
    modalBodyVariant: 'funded',
    isTopPrize: true,
  },
];

export const SEGMENT_COUNT = PRIZES.length;
export const SEGMENT_ANGLE = 360 / SEGMENT_COUNT; // 72°

export const TOTAL_WEIGHT = PRIZES.reduce((sum, p) => sum + p.weight, 0);

export const FEATURED_PRIZE = PRIZES.find((p) => p.isTopPrize)!;
export const SECONDARY_PRIZES = PRIZES.filter((p) => !p.isTopPrize);

export function prizeById(id: PrizeId): Prize {
  const found = PRIZES.find((p) => p.id === id);
  if (!found) throw new Error(`Unknown prize id: ${id}`);
  return found;
}

function random(): number {
  const c = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
  if (!c?.getRandomValues) return Math.random();

  const buf = new Uint32Array(1);
  const limit = 2 ** 32 - (2 ** 32 % TOTAL_WEIGHT);
  do {
    c.getRandomValues(buf);
  } while (buf[0] >= limit);
  return buf[0] / limit;
}

export function drawPrize(): Prize {
  let roll = random() * TOTAL_WEIGHT;
  for (const prize of PRIZES) {
    roll -= prize.weight;
    if (roll < 0) return prize;
  }
  return PRIZES[PRIZES.length - 1];
}

export function angleForPrize(prize: Prize, jitter = 0, restOffset = 0): number {
  const index = PRIZES.indexOf(prize);
  const centre = restOffset + (index + 0.5) * SEGMENT_ANGLE;
  const maxOffset = SEGMENT_ANGLE * 0.34;
  const clamped = Math.max(-1, Math.min(1, jitter));
  return -(centre + clamped * maxOffset);
}

export function spinRotation(
  from: number,
  prize: Prize,
  turns: number,
  jitter = 0,
  restOffset = 0,
): number {
  const target = angleForPrize(prize, jitter, restOffset);
  const delta = ((target - from) % 360 + 360) % 360;
  return from + turns * 360 + delta;
}
